export const PCAP_MAX_PACKETS = 1000;
export const PCAP_MAX_PARSE_BYTES = 16 * 1024 * 1024;
export const PCAP_HEX_DUMP_MAX = 4096;

export type CaptureFormat = 'pcap' | 'pcapng';
export type PacketProtocol = 'TCP' | 'UDP' | 'ICMP' | 'ARP' | 'IPv6' | 'Other';

export interface PacketField {
  label: string;
  value: string;
}

export interface PacketSummary {
  index: number;
  timestampMs: number;
  length: number;
  src: string;
  dst: string;
  protocol: PacketProtocol;
  srcPort: number | null;
  dstPort: number | null;
  payloadOffset: number | null;
  payloadLength: number | null;
  streamKey: string | null;
  streamId: number | null;

  // TCP-only (only set when protocol is 'TCP')
  tcpSeq: number | null;
  tcpAck: number | null;
  tcpWindow: number | null;
  tcpHeaderLength: number | null;

  // UDP-only (only set when protocol is 'UDP')
  udpLength: number | null;
  info: string;
  bytes: Uint8Array;
  fields: PacketField[];
}

export interface CapturePreview {
  format: CaptureFormat;
  linkType: number;
  packetCount: number;
  truncated: boolean;
  firstTs: number | null;
  lastTs: number | null;
  packets: PacketSummary[];
}

export interface ParseCaptureOptions {
  maxPackets?: number;
  maxBytes?: number;
}

const DLT_NULL = 0;
const DLT_EN10MB = 1;
const DLT_RAW = 12;
const DLT_RAW_ALT = 14;
const DLT_LINUX_SLL = 113;

const PCAPNG_SHB = 0x0a0d0d0a;
const PCAPNG_IDB = 0x00000001;
const PCAPNG_SPB = 0x00000003;
const PCAPNG_EPB = 0x00000006;
const PCAPNG_BOM_LE = 0x1a2b3c4d;

const TCP_FLAG_NAMES: Array<[number, string]> = [
  [0x01, 'FIN'],
  [0x02, 'SYN'],
  [0x04, 'RST'],
  [0x08, 'PSH'],
  [0x10, 'ACK'],
  [0x20, 'URG'],
  [0x40, 'ECE'],
  [0x80, 'CWR'],
];

interface PcapngIface {
  linkType: number;
  tsresol: number;
  tsresolBinary: boolean;
}

export function parseCapture(data: Uint8Array, options: ParseCaptureOptions = {}): CapturePreview | null {
  if (data.length < 4) return null;
  const maxPackets = options.maxPackets ?? PCAP_MAX_PACKETS;
  const maxBytes = options.maxBytes ?? PCAP_MAX_PARSE_BYTES;

  if (data[0] === 0x0a && data[1] === 0x0d && data[2] === 0x0d && data[3] === 0x0a) {
    return parsePcapng(data, maxPackets, maxBytes);
  }

  const endian = classicPcapEndian(data);
  if (!endian) return null;
  return parseClassicPcap(data, endian.le, endian.nano, maxPackets, maxBytes);
}

function classicPcapEndian(data: Uint8Array): { le: boolean; nano: boolean } | null {
  const b0 = data[0];
  const b1 = data[1];
  const b2 = data[2];
  const b3 = data[3];
  if (b0 === 0xd4 && b1 === 0xc3 && b2 === 0xb2 && b3 === 0xa1) return { le: true, nano: false };
  if (b0 === 0xa1 && b1 === 0xb2 && b2 === 0xc3 && b3 === 0xd4) return { le: false, nano: false };
  if (b0 === 0x4d && b1 === 0x3c && b2 === 0xb2 && b3 === 0xa1) return { le: true, nano: true };
  if (b0 === 0xa1 && b1 === 0xb2 && b2 === 0x3c && b3 === 0x4d) return { le: false, nano: true };
  return null;
}

function parseClassicPcap(
  data: Uint8Array,
  le: boolean,
  nano: boolean,
  maxPackets: number,
  maxBytes: number,
): CapturePreview | null {
  if (data.length < 24) return null;
  const view = viewOf(data);
  const linkType = view.getUint32(20, le);
  const packets: PacketSummary[] = [];
  let offset = 24;
  let truncated = false;
  const scanLimit = Math.min(data.length, maxBytes);

  while (offset + 16 <= scanLimit) {
    if (packets.length >= maxPackets) {
      truncated = true;
      break;
    }
    const tsSec = view.getUint32(offset, le);
    const tsFrac = view.getUint32(offset + 4, le);
    const inclLen = view.getUint32(offset + 8, le);
    const origLen = view.getUint32(offset + 12, le);
    offset += 16;
    if (inclLen > 0x1000000 || offset + inclLen > data.length) {
      truncated = true;
      break;
    }
    if (offset + inclLen > scanLimit) {
      truncated = true;
      break;
    }
    const frame = data.subarray(offset, offset + inclLen);
    const timestampMs = nano ? tsSec * 1000 + tsFrac / 1_000_000 : tsSec * 1000 + tsFrac / 1000;
    packets.push(decodeFrame(frame, linkType, packets.length + 1, timestampMs, origLen || inclLen));
    offset += inclLen;
  }

  if (offset < data.length) truncated = true;

  return finishCapture('pcap', linkType, packets, truncated);
}

function parsePcapng(data: Uint8Array, maxPackets: number, maxBytes: number): CapturePreview | null {
  if (data.length < 12) return null;
  const bomBytes = data.subarray(8, 12);
  let le: boolean;
  if (bomBytes[0] === 0x4d && bomBytes[1] === 0x3c && bomBytes[2] === 0x2b && bomBytes[3] === 0x1a) {
    le = true;
  } else if (bomBytes[0] === 0x1a && bomBytes[1] === 0x2b && bomBytes[2] === 0x3c && bomBytes[3] === 0x4d) {
    le = false;
  } else {
    return null;
  }

  const view = viewOf(data);
  const interfaces: PcapngIface[] = [];
  const packets: PacketSummary[] = [];
  let offset = 0;
  let truncated = false;
  const scanLimit = Math.min(data.length, maxBytes);

  while (offset + 12 <= scanLimit) {
    if (packets.length >= maxPackets) {
      truncated = true;
      break;
    }
    const blockType = view.getUint32(offset, le);
    const totalLen = view.getUint32(offset + 4, le);
    if (totalLen < 12 || totalLen > data.length || offset + totalLen > data.length) {
      truncated = packets.length > 0;
      break;
    }
    if (offset + totalLen > scanLimit && blockType !== PCAPNG_SHB) {
      truncated = true;
      break;
    }

    const body = data.subarray(offset + 8, offset + totalLen - 4);

    if (blockType === PCAPNG_SHB) {
      const magic = view.getUint32(offset + 8, le);
      if (magic !== PCAPNG_BOM_LE) return packets.length > 0 ? finishCapture('pcapng', firstLink(interfaces), packets, true) : null;
    } else if (blockType === PCAPNG_IDB) {
      if (body.length >= 8) {
        const ifaceView = viewOf(body);
        interfaces.push({
          linkType: ifaceView.getUint16(0, le),
          ...parseIdbTsresol(body, le),
        });
      }
    } else if (blockType === PCAPNG_EPB) {
      if (body.length >= 20) {
        const b = viewOf(body);
        const ifId = b.getUint32(0, le);
        const tsHigh = b.getUint32(4, le);
        const tsLow = b.getUint32(8, le);
        const capLen = b.getUint32(12, le);
        const origLen = b.getUint32(16, le);
        if (20 + capLen <= body.length) {
          const iface = interfaces[ifId] ?? defaultIface(interfaces);
          const frame = body.subarray(20, 20 + capLen);
          const raw = (BigInt(tsHigh) << 32n) + BigInt(tsLow);
          const timestampMs = timestampToMs(raw, iface.tsresol, iface.tsresolBinary);
          packets.push(decodeFrame(frame, iface.linkType, packets.length + 1, timestampMs, origLen || capLen));
        }
      }
    } else if (blockType === PCAPNG_SPB) {
      if (body.length >= 4) {
        const b = viewOf(body);
        const origLen = b.getUint32(0, le);
        const capLen = Math.min(origLen, body.length - 4);
        const iface = defaultIface(interfaces);
        const frame = body.subarray(4, 4 + capLen);
        packets.push(decodeFrame(frame, iface.linkType, packets.length + 1, 0, origLen));
      }
    }

    offset += totalLen;
  }

  if (offset < data.length) truncated = true;
  if (packets.length === 0 && interfaces.length === 0 && offset === 0) return null;
  return finishCapture('pcapng', firstLink(interfaces), packets, truncated);
}

function parseIdbTsresol(body: Uint8Array, le: boolean): Pick<PcapngIface, 'tsresol' | 'tsresolBinary'> {
  const result: Pick<PcapngIface, 'tsresol' | 'tsresolBinary'> = { tsresol: 6, tsresolBinary: false };
  let offset = 8;
  const view = viewOf(body);
  while (offset + 4 <= body.length) {
    const code = view.getUint16(offset, le);
    const length = view.getUint16(offset + 2, le);
    offset += 4;
    if (code === 0) break;
    if (code === 9 && length >= 1 && offset < body.length) {
      const value = body[offset];
      if (value & 0x80) {
        result.tsresolBinary = true;
        result.tsresol = value & 0x7f;
      } else {
        result.tsresolBinary = false;
        result.tsresol = value;
      }
    }
    offset += (length + 3) & ~3;
  }
  return result;
}

function defaultIface(interfaces: PcapngIface[]): PcapngIface {
  return interfaces[0] ?? { linkType: DLT_EN10MB, tsresol: 6, tsresolBinary: false };
}

function firstLink(interfaces: PcapngIface[]): number {
  return interfaces[0]?.linkType ?? DLT_EN10MB;
}

function timestampToMs(raw: bigint, resol: number, binary: boolean): number {
  if (binary) {
    const seconds = Number(raw) / 2 ** resol;
    return seconds * 1000;
  }
  if (resol >= 3) {
    const div = 10n ** BigInt(resol - 3);
    return Number(raw / div);
  }
  return Number(raw) * 10 ** (3 - resol);
}

function finishCapture(
  format: CaptureFormat,
  linkType: number,
  packets: PacketSummary[],
  truncated: boolean,
): CapturePreview {
  // Assign a stable stream id based on first appearance.
  // This enables “follow/hide stream” in the preview UI.
  const streamIds = new Map<string, number>();
  for (const packet of packets) {
    if (!packet.streamKey) continue;
    const existing = streamIds.get(packet.streamKey);
    if (existing != null) {
      packet.streamId = existing;
      continue;
    }
    const id = streamIds.size;
    streamIds.set(packet.streamKey, id);
    packet.streamId = id;
  }

  return {
    format,
    linkType,
    packetCount: packets.length,
    truncated,
    firstTs: packets[0]?.timestampMs ?? null,
    lastTs: packets[packets.length - 1]?.timestampMs ?? null,
    packets,
  };
}

function decodeFrame(
  frame: Uint8Array,
  linkType: number,
  index: number,
  timestampMs: number,
  length: number,
): PacketSummary {
  const packet: PacketSummary = {
    index,
    timestampMs,
    length,
    src: '',
    dst: '',
    protocol: 'Other',
    srcPort: null,
    dstPort: null,
    payloadOffset: null,
    payloadLength: null,
    streamKey: null,
    streamId: null,

    tcpSeq: null,
    tcpAck: null,
    tcpWindow: null,
    tcpHeaderLength: null,

    udpLength: null,
    info: `Link type ${linkType}`,
    bytes: frame,
    fields: [
      { label: 'Length', value: String(length) },
      { label: 'Link type', value: String(linkType) },
    ],
  };

  try {
    switch (linkType) {
      case DLT_EN10MB:
        return decodeEthernet(frame, packet);
      case DLT_LINUX_SLL:
        return decodeLinuxSll(frame, packet);
      case DLT_RAW:
      case DLT_RAW_ALT:
        return decodeIp(frame, 0, packet);
      case DLT_NULL:
        return packet;
      default:
        return packet;
    }
  } catch {
    return packet;
  }
}

function decodeEthernet(frame: Uint8Array, packet: PacketSummary): PacketSummary {
  if (frame.length < 14) return packet;
  packet.dst = formatMac(frame, 0);
  packet.src = formatMac(frame, 6);
  packet.fields.push(
    { label: 'Destination MAC', value: packet.dst },
    { label: 'Source MAC', value: packet.src },
  );
  let ethertype = readU16be(frame, 12);
  let offset = 14;
  if ((ethertype === 0x8100 || ethertype === 0x88a8) && frame.length >= offset + 4) {
    ethertype = readU16be(frame, offset + 2);
    offset += 4;
  }
  packet.fields.push({ label: 'EtherType', value: `0x${ethertype.toString(16).padStart(4, '0')}` });
  return decodeEtherType(frame, offset, ethertype, packet);
}

function decodeLinuxSll(frame: Uint8Array, packet: PacketSummary): PacketSummary {
  if (frame.length < 16) return packet;
  const addrLen = Math.min(readU16be(frame, 4), 8);
  packet.src = addrLen === 6 ? formatMac(frame, 6) : '';
  const protocol = readU16be(frame, 14);
  if (packet.src) packet.fields.push({ label: 'Source MAC', value: packet.src });
  packet.fields.push({ label: 'Protocol', value: `0x${protocol.toString(16).padStart(4, '0')}` });
  return decodeEtherType(frame, 16, protocol, packet);
}

function decodeEtherType(frame: Uint8Array, offset: number, ethertype: number, packet: PacketSummary): PacketSummary {
  if (ethertype === 0x0800) return decodeIpv4(frame, offset, packet);
  if (ethertype === 0x86dd) return decodeIpv6(frame, offset, packet);
  if (ethertype === 0x0806) return decodeArp(frame, offset, packet);
  packet.protocol = 'Other';
  packet.info = `EtherType 0x${ethertype.toString(16)}`;
  return packet;
}

function decodeIp(frame: Uint8Array, offset: number, packet: PacketSummary): PacketSummary {
  if (offset >= frame.length) return packet;
  const version = frame[offset] >> 4;
  if (version === 4) return decodeIpv4(frame, offset, packet);
  if (version === 6) return decodeIpv6(frame, offset, packet);
  packet.protocol = 'Other';
  packet.info = 'Unknown IP version';
  return packet;
}

function decodeIpv4(frame: Uint8Array, offset: number, packet: PacketSummary): PacketSummary {
  if (frame.length < offset + 20) return packet;
  const ihl = (frame[offset] & 0x0f) * 4;
  if (ihl < 20 || frame.length < offset + ihl) return packet;
  const proto = frame[offset + 9];
  packet.src = formatIpv4(frame, offset + 12);
  packet.dst = formatIpv4(frame, offset + 16);
  packet.fields.push(
    { label: 'Source', value: packet.src },
    { label: 'Destination', value: packet.dst },
    { label: 'TTL', value: String(frame[offset + 8]) },
  );
  return decodeL4(frame, offset + ihl, proto, packet);
}

function decodeIpv6(frame: Uint8Array, offset: number, packet: PacketSummary): PacketSummary {
  if (frame.length < offset + 40) return packet;
  const nextHeader = frame[offset + 6];
  packet.src = formatIpv6(frame, offset + 8);
  packet.dst = formatIpv6(frame, offset + 24);
  packet.fields.push(
    { label: 'Source', value: packet.src },
    { label: 'Destination', value: packet.dst },
  );
  if (nextHeader === 6 || nextHeader === 17 || nextHeader === 58) {
    return decodeL4(frame, offset + 40, nextHeader === 58 ? 1 : nextHeader, packet);
  }
  packet.protocol = 'IPv6';
  packet.info = `IPv6 next header ${nextHeader}`;
  return packet;
}

function decodeL4(frame: Uint8Array, offset: number, proto: number, packet: PacketSummary): PacketSummary {
  if (proto === 6) return decodeTcp(frame, offset, packet);
  if (proto === 17) return decodeUdp(frame, offset, packet);
  if (proto === 1) return decodeIcmp(frame, offset, packet);
  packet.protocol = 'Other';
  packet.info = `IP protocol ${proto}`;
  return packet;
}

function decodeTcp(frame: Uint8Array, offset: number, packet: PacketSummary): PacketSummary {
  if (frame.length < offset + 20) return packet;
  packet.protocol = 'TCP';
  packet.srcPort = readU16be(frame, offset);
  packet.dstPort = readU16be(frame, offset + 2);

  const seq = readU32be(frame, offset + 4);
  const ack = readU32be(frame, offset + 8);
  const dataOffsetWords = (frame[offset + 12] >> 4) & 0x0f;
  const headerLength = Math.max(20, dataOffsetWords * 4);
  const payloadOffset = offset + headerLength;
  const payloadAvailable = Math.max(0, frame.length - payloadOffset);

  packet.tcpSeq = seq;
  packet.tcpAck = ack;
  packet.tcpHeaderLength = headerLength;

  const flags = frame[offset + 13];
  const flagNames = TCP_FLAG_NAMES.filter(([bit]) => (flags & bit) !== 0).map(([, name]) => name);
  const flagText = flagNames.length > 0 ? flagNames.join(', ') : String(flags);
  packet.tcpWindow = readU16be(frame, offset + 14);

  packet.payloadOffset = payloadOffset;
  packet.payloadLength = payloadAvailable;
  packet.streamKey = canonicalStreamKey('TCP', packet.src, packet.srcPort!, packet.dst, packet.dstPort!);

  packet.info = `${packet.srcPort} \u2192 ${packet.dstPort} [${flagText}] Seq=${seq} Ack=${ack} Len=${payloadAvailable}`;
  packet.fields.push(
    { label: 'Src port', value: String(packet.srcPort) },
    { label: 'Dst port', value: String(packet.dstPort) },
    { label: 'TCP flags', value: flagText },
    { label: 'Seq', value: String(seq) },
    { label: 'Ack', value: String(ack) },
    { label: 'Window', value: String(packet.tcpWindow) },
    { label: 'TCP header length', value: String(headerLength) },
    { label: 'Payload length', value: String(payloadAvailable) },
  );
  return packet;
}

function decodeUdp(frame: Uint8Array, offset: number, packet: PacketSummary): PacketSummary {
  if (frame.length < offset + 8) return packet;
  packet.protocol = 'UDP';
  packet.srcPort = readU16be(frame, offset);
  packet.dstPort = readU16be(frame, offset + 2);

  const udpLength = readU16be(frame, offset + 4);
  packet.udpLength = udpLength;

  const payloadOffset = offset + 8;
  const payloadAvailable = Math.max(0, frame.length - payloadOffset);
  const payloadFromHeader = udpLength >= 8 ? udpLength - 8 : 0;
  const payloadLength = Math.min(payloadAvailable, payloadFromHeader);

  packet.payloadOffset = payloadOffset;
  packet.payloadLength = payloadLength;
  packet.streamKey = canonicalStreamKey('UDP', packet.src, packet.srcPort!, packet.dst, packet.dstPort!);

  packet.info = `${packet.srcPort} \u2192 ${packet.dstPort} Len=${payloadLength}`;
  packet.fields.push(
    { label: 'Src port', value: String(packet.srcPort) },
    { label: 'Dst port', value: String(packet.dstPort) },
    { label: 'UDP length', value: String(udpLength) },
    { label: 'Payload length', value: String(payloadLength) },
  );
  return packet;
}

function decodeIcmp(frame: Uint8Array, offset: number, packet: PacketSummary): PacketSummary {
  packet.protocol = 'ICMP';
  if (frame.length < offset + 2) {
    packet.info = 'ICMP';
    return packet;
  }
  const type = frame[offset];
  const code = frame[offset + 1];
  packet.info = `type ${type} code ${code}`;
  packet.fields.push(
    { label: 'ICMP type', value: String(type) },
    { label: 'ICMP code', value: String(code) },
  );
  return packet;
}

function decodeArp(frame: Uint8Array, offset: number, packet: PacketSummary): PacketSummary {
  packet.protocol = 'ARP';
  if (frame.length < offset + 8) {
    packet.info = 'ARP';
    return packet;
  }
  const hwLen = frame[offset + 4];
  const protoLen = frame[offset + 5];
  const opcode = readU16be(frame, offset + 6);
  const shaOff = offset + 8;
  const spaOff = shaOff + hwLen;
  const thaOff = spaOff + protoLen;
  const tpaOff = thaOff + hwLen;
  if (hwLen === 6 && protoLen === 4 && frame.length >= tpaOff + 4) {
    const spa = formatIpv4(frame, spaOff);
    const tpa = formatIpv4(frame, tpaOff);
    const sha = formatMac(frame, shaOff);
    packet.src = spa;
    packet.dst = tpa;
    packet.info = opcode === 1 ? `Who has ${tpa}? Tell ${spa}` : opcode === 2 ? `${spa} is at ${sha}` : `opcode ${opcode}`;
    packet.fields.push(
      { label: 'Source', value: spa },
      { label: 'Destination', value: tpa },
      { label: 'Opcode', value: String(opcode) },
    );
    return packet;
  }
  packet.info = `ARP opcode ${opcode}`;
  return packet;
}

export function formatHexDump(bytes: Uint8Array, maxBytes = PCAP_HEX_DUMP_MAX): string {
  const slice = bytes.subarray(0, maxBytes);
  const lines: string[] = [];
  for (let i = 0; i < slice.length; i += 16) {
    const chunk = slice.subarray(i, Math.min(i + 16, slice.length));
    const hexParts: string[] = [];
    for (let j = 0; j < 16; j += 1) {
      hexParts.push(j < chunk.length ? chunk[j].toString(16).padStart(2, '0') : '  ');
      if (j === 7) hexParts.push('');
    }
    const ascii = Array.from(chunk, (b) => (b >= 32 && b < 127 ? String.fromCharCode(b) : '.')).join('');
    lines.push(`${i.toString(16).padStart(4, '0')}  ${hexParts.join(' ').trimEnd().padEnd(48, ' ')}  ${ascii}`);
  }
  if (bytes.length > maxBytes) {
    lines.push(`\u2026 ${bytes.length - maxBytes} more bytes`);
  }
  return lines.join('\n');
}

function viewOf(data: Uint8Array): DataView {
  return new DataView(data.buffer, data.byteOffset, data.byteLength);
}

function readU16be(data: Uint8Array, offset: number): number {
  return (data[offset] << 8) | data[offset + 1];
}

function readU32be(data: Uint8Array, offset: number): number {
  // Use unsigned math to avoid sign-extension from bitwise operators.
  return (((data[offset] << 24) | (data[offset + 1] << 16) | (data[offset + 2] << 8) | data[offset + 3]) >>> 0);
}

function canonicalStreamKey(
  proto: 'TCP' | 'UDP',
  aIp: string,
  aPort: number,
  bIp: string,
  bPort: number,
): string {
  const a = { ip: aIp, port: aPort };
  const b = { ip: bIp, port: bPort };

  const cmp =
    a.ip < b.ip ? -1
    : a.ip > b.ip ? 1
    : a.port < b.port ? -1
    : a.port > b.port ? 1
    : 0;

  const first = cmp <= 0 ? a : b;
  const second = cmp <= 0 ? b : a;
  return `${proto}|${first.ip}:${first.port}-${second.ip}:${second.port}`;
}

function formatMac(data: Uint8Array, offset: number): string {
  return Array.from(data.subarray(offset, offset + 6), (b) => b.toString(16).padStart(2, '0')).join(':');
}

function formatIpv4(data: Uint8Array, offset: number): string {
  return `${data[offset]}.${data[offset + 1]}.${data[offset + 2]}.${data[offset + 3]}`;
}

function formatIpv6(data: Uint8Array, offset: number): string {
  const parts: string[] = [];
  for (let i = 0; i < 16; i += 2) {
    parts.push(((data[offset + i] << 8) | data[offset + i + 1]).toString(16));
  }
  return parts.join(':');
}
